import { useEffect, useState } from "react";
import { imgUrl } from "@/lib/api";

const cache = new Map();

/** The photograph's own colour, sampled from a tiny copy – for a soft glow behind the frame. Same-origin pictures only;
 *  anything that taints the canvas simply yields null (no glow, no error). */
export const useDominantColor = (src) => {
  const [color, setColor] = useState(() => (src ? cache.get(src) || null : null));
  useEffect(() => {
    if (!src) { setColor(null); return undefined; }
    if (cache.has(src)) { setColor(cache.get(src)); return undefined; }
    let alive = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.decoding = "async";
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = 24; c.height = 24;
        const ctx = c.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, 24, 24);
        const d = ctx.getImageData(0, 0, 24, 24).data;
        let r = 0, g = 0, b = 0, n = 0;
        for (let i = 0; i < d.length; i += 4) {
          const max = Math.max(d[i], d[i + 1], d[i + 2]), min = Math.min(d[i], d[i + 1], d[i + 2]);
          const w = 0.35 + (max - min) / 255;  // favour the saturated pixels – the colour you remember
          r += d[i] * w; g += d[i + 1] * w; b += d[i + 2] * w; n += w;
        }
        const out = `rgb(${Math.round(r / n)}, ${Math.round(g / n)}, ${Math.round(b / n)})`;
        cache.set(src, out);
        if (alive) setColor(out);
      } catch { cache.set(src, null); if (alive) setColor(null); }
    };
    img.onerror = () => { cache.set(src, null); if (alive) setColor(null); };
    img.src = imgUrl(src, 320);
    return () => { alive = false; };
  }, [src]);
  return color;
};
